/* ==========================================================================
   Kopstorm quick add — Celeste D'or Curve
   Two product card quick add modes, runtime switchable for Intelligems:

     inline : hover (desktop) / plus button (mobile) reveals color + size
              rows on the card; clicking a size adds that variant to cart.
     modal  : Quick add button opens a dedicated popup (markup in
              snippets/cdc-quick-add-popup.liquid) with image grid or
              Splide slider, full option selection, urgency and size guide.

   Mode resolution order:
     1. window.CDC_QA_MODE            (set per Intelligems variant)
     2. window.CDC_QA_SETTINGS.mode   (theme setting, injected in theme.liquid)

   Intelligems can also switch after load: window.CDCQuickAdd.setMode('modal')

   Product data is fetched lazily from {product url}.js on first hover,
   viewport entry (inline mode) or tap, and cached.
   ========================================================================== */
(function () {
  'use strict';

  var settings = window.CDC_QA_SETTINGS || {};
  var COLOR_NAMES = (settings.colorOptionNames || 'Color, Colour')
    .split(',')
    .map(function (s) { return s.trim().toLowerCase(); })
    .filter(Boolean);
  var SPLIDE_VERSION = '4.1.4';
  var URGENCY_MIN = 3;
  var URGENCY_MAX = 9;

  var productCache = {};

  /* ---------------------------------------------------------------- mode */

  function currentMode() {
    return document.documentElement.getAttribute('data-cdc-qa') || 'theme';
  }

  function setMode(mode) {
    if (['inline', 'modal', 'theme'].indexOf(mode) === -1) mode = 'theme';
    document.documentElement.setAttribute('data-cdc-qa', mode);
    if (mode === 'inline') observeAll();
  }

  setMode(window.CDC_QA_MODE || settings.mode || 'theme');

  /* Inline panel style override, switchable per Intelligems variant.
     'dark' forces the dark panel regardless of the theme setting;
     'light' / null falls back to the customizer setting. */
  function setTheme(theme) {
    if (theme === 'dark') {
      document.documentElement.setAttribute('data-cdc-qa-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-cdc-qa-theme');
    }
  }

  if (window.CDC_QA_THEME) setTheme(window.CDC_QA_THEME);

  /* ------------------------------------------------------------- helpers */

  function isColorOption(name) {
    return COLOR_NAMES.indexOf(String(name).trim().toLowerCase()) !== -1;
  }

  function sized(src, width) {
    if (!src) return src;
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function parseVariantParam(url) {
    var match = /[?&]variant=(\d+)/.exec(url || '');
    return match ? parseInt(match[1], 10) : null;
  }

  function formatMoney(cents, moneyFormat) {
    var amount = (cents / 100).toFixed(2);
    return (moneyFormat || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100));
  }

  /* Transform /products/{handle}.js into the internal shape */
  function transformProduct(p) {
    var media = (p.media || []).filter(function (m) { return m.media_type === 'image'; });
    var images = media.length
      ? media.map(function (m) { return { src: sized(m.src, 700), alt: m.alt || p.title }; })
      : (p.images || []).map(function (src) { return { src: sized(src, 700), alt: p.title }; });

    /* Strip style/script blocks BEFORE reading text, otherwise CSS rules
       inside the description leak into the excerpt */
    var descriptionShort = '';
    if (p.description) {
      var tmp = document.createElement('div');
      tmp.innerHTML = p.description;
      tmp.querySelectorAll('style, script, noscript').forEach(function (el) { el.remove(); });
      descriptionShort = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
      if (descriptionShort.length > 140) descriptionShort = descriptionShort.slice(0, 137) + '...';
    }

    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      url: p.url,
      price: p.price,
      options: (p.options || []).map(function (opt) {
        return { name: opt.name, position: opt.position, values: opt.values.slice() };
      }),
      variants: (p.variants || []).map(function (v) {
        return {
          id: v.id,
          title: v.title,
          price: v.price,
          compare_at_price: v.compare_at_price || null,
          available: v.available,
          options: [v.option1, v.option2, v.option3].filter(function (o) { return o != null; }),
          featured_image: v.featured_image ? { src: sized(v.featured_image.src, 700) } : null
        };
      }),
      images: images,
      description_short: descriptionShort
    };
  }

  function fetchProduct(productUrl) {
    var path = productUrl.split('#')[0].split('?')[0].replace(/\/$/, '');
    /* Normalize collection-scoped URLs (/collections/x/products/y) to the
       canonical product path, keeping any locale root prefix */
    var match = path.match(/\/products\/[^/]+$/);
    if (match) {
      var localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root.replace(/\/$/, '')
        : '';
      path = localeRoot + match[0];
    }
    var jsUrl = path + '.js';
    if (!productCache[jsUrl]) {
      productCache[jsUrl] = fetch(jsUrl, { headers: { Accept: 'application/json' } })
        .then(function (resp) {
          if (!resp.ok) throw new Error('Product fetch failed');
          return resp.json();
        })
        .then(transformProduct)
        .catch(function (err) {
          delete productCache[jsUrl];
          throw err;
        });
    }
    return productCache[jsUrl];
  }

  function ensureData(wrapper) {
    return fetchProduct(wrapper.dataset.productUrl || '');
  }

  function preloadImages(data, limit) {
    (data.images || []).slice(0, limit || 8).forEach(function (img) {
      var pre = new Image();
      pre.src = img.src;
    });
  }

  /* ------------------------------------------------------------ cart add */

  function addToCart(variantId) {
    var sectionsToUpdate = ['page-header', 'cart-drawer']
      .map(function (sel) { return document.querySelector(sel); })
      .filter(function (el) { return el; });
    var sectionIds = sectionsToUpdate.map(function (el) { return el.dataset.sectionId; });
    var cartUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js';

    return fetch(cartUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname
      })
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error('Add failed');
        return resp.json();
      })
      .then(function (data) {
        if (data.sections && sectionsToUpdate.length) {
          sectionsToUpdate.forEach(function (el) {
            var html = data.sections[el.dataset.sectionId];
            if (html && typeof el.updateFromCartChange === 'function') {
              el.updateFromCartChange(html);
            }
          });
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }
        var cartDrawer = document.querySelector('.js-cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      });
  }

  /* ------------------------------------------------------------- urgency */

  function urgencyCookie(productId, variantId, count) {
    var name = 'urgency_stock_' + productId + '_' + variantId;
    if (count != null) {
      document.cookie = name + '=' + count + '; max-age=3600; path=/; SameSite=Lax';
      return count;
    }
    var m = document.cookie.match(new RegExp('(^| )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]+)'));
    return m ? parseInt(m[2], 10) : null;
  }

  function urgencyCount(productId, variantId) {
    var count = urgencyCookie(productId, variantId);
    if (count == null || count < URGENCY_MIN || count > URGENCY_MAX) {
      count = Math.floor(Math.random() * (URGENCY_MAX - URGENCY_MIN + 1)) + URGENCY_MIN;
      urgencyCookie(productId, variantId, count);
    }
    return count;
  }

  /* --------------------------------------------------------- inline mode */

  function findVariant(data, selections) {
    return data.variants.find(function (v) {
      return data.options.every(function (opt, i) {
        return v.options[i] === selections[opt.name];
      });
    });
  }

  function optionAvailable(data, selections, optionName, value) {
    var test = {};
    Object.keys(selections).forEach(function (k) { test[k] = selections[k]; });
    test[optionName] = value;
    return data.variants.some(function (v) {
      if (!v.available) return false;
      return data.options.every(function (opt, i) {
        if (!test[opt.name]) return true;
        return v.options[i] === test[opt.name];
      });
    });
  }

  /* --------------------------------------------------- native swatches */

  /* The theme paints [data-swatch] circles from assets/swatches.css, which only
     emits rules when swatch_source is "theme" AND swatch_value_list is filled
     (the US setup). Stores on Shopify's native swatches (UK, AU) get an empty
     stylesheet, so the circles fall back to the plain grey default. There the
     colours come from cdc-quick-add.liquid as a flat string on the panel:
     "black~c~0, 0, 0|nude~i~https://...", entries split on a pipe, fields on a
     tilde. No attribute = the theme stylesheet keeps doing the work, so the US
     behaviour is unchanged. */
  function swatchMap(wrapper) {
    if (!wrapper) return null;
    if (wrapper.cdcSwatchMap !== undefined) return wrapper.cdcSwatchMap;
    var panel = wrapper.querySelector('[data-cdc-qa-panel]');
    var raw = panel ? panel.getAttribute('data-cdc-qa-swatches') : null;
    var map = null;
    if (raw) {
      map = {};
      raw.split('|').forEach(function (entry) {
        var parts = entry.split('~');
        if (parts.length < 3 || !parts[0]) return;
        map[parts[0]] = { kind: parts[1], value: parts.slice(2).join('~') };
      });
    }
    wrapper.cdcSwatchMap = map;
    return map;
  }

  /* Whether an option is the colour option. The name list alone is not enough:
     swatch_option_name is a per-store text setting (AU only lists "Color"), so
     any product whose option is spelled differently rendered its colours as
     plain text buttons. The swatch map is the same source the theme itself uses
     to decide (values | where: 'swatch'), so treat an option as colour when its
     values carry swatches, and keep the name list as the fallback for stores on
     the theme swatch list where no map is emitted. */
  function optionIsColor(opt, map) {
    if (isColorOption(opt.name)) return true;
    if (!map || !opt || !opt.values || !opt.values.length) return false;
    var matched = 0;
    opt.values.forEach(function (value) {
      var valName = typeof value === 'object' ? (value.value || String(value)) : String(value);
      if (map[valName.replace(/"/g, '').toLowerCase().trim()]) matched += 1;
    });
    return matched > 0 && matched * 2 >= opt.values.length;
  }

  function applySwatch(btn, map, valName) {
    if (!map) return;
    var entry = map[valName.replace(/"/g, '').toLowerCase().trim()];
    if (!entry || !entry.value) return;
    if (entry.kind === 'c') {
      btn.style.setProperty('--swatch-background-color', 'rgb(' + entry.value + ')');
    } else if (entry.kind === 'i') {
      btn.style.setProperty('--swatch-background-image', 'url(' + entry.value + ')');
    }
  }

  var CHECK_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="20 6 9 17 4 12"/></svg>';
  var SPINNER_HTML = '<span class="cdc-qa-spinner cdc-qa-spinner--small"></span>';

  function buildPanel(wrapper, data) {
    var panel = wrapper.querySelector('[data-cdc-qa-panel]');
    if (!panel || panel.dataset.built === 'true') return;
    panel.dataset.built = 'true';

    var swMap = swatchMap(wrapper);
    var selections = {};
    var preselectId = parseVariantParam(wrapper.dataset.productUrl);
    var preselectVariant = null;
    if (preselectId) {
      preselectVariant = data.variants.find(function (v) { return v.id === preselectId; });
    }
    if (!preselectVariant) {
      preselectVariant = data.variants.find(function (v) { return v.available; }) || data.variants[0];
    }

    var label = document.createElement('span');
    label.className = 'cdc-qa__panel-label';
    label.textContent = 'Quick add';
    panel.appendChild(label);

    /* Last non-color option is the row that triggers add to cart */
    var triggerOptionName = null;
    data.options.forEach(function (opt) {
      if (!optionIsColor(opt, swMap)) triggerOptionName = opt.name;
    });
    if (!triggerOptionName && data.options.length) {
      triggerOptionName = data.options[data.options.length - 1].name;
    }

    var rows = [];

    data.options.forEach(function (opt, optIndex) {
      var isColor = optionIsColor(opt, swMap);
      var isTrigger = opt.name === triggerOptionName;
      var row = document.createElement('div');
      row.className = 'cdc-qa__row' + (isColor ? ' cdc-qa__row--swatches' : '');
      row.dataset.optionName = opt.name;

      if (!isTrigger && preselectVariant) {
        selections[opt.name] = preselectVariant.options[optIndex];
      }

      opt.values.forEach(function (value) {
        var valName = typeof value === 'object' ? (value.value || String(value)) : String(value);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.value = valName;

        if (isColor) {
          btn.className = 'cdc-qa__swatch';
          btn.setAttribute('data-swatch', valName.replace(/"/g, '').toLowerCase());
          applySwatch(btn, swMap, valName);
          btn.setAttribute('aria-label', valName);
          btn.title = valName;
        } else {
          btn.className = 'cdc-qa__size';
          btn.textContent = valName;
        }

        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();

          if (isTrigger) {
            selections[opt.name] = valName;
            var variant = findVariant(data, selections);
            if (!variant || !variant.available || btn.disabled || btn.dataset.busy === 'true') return;
            btn.dataset.busy = 'true';
            var original = btn.textContent;
            btn.innerHTML = SPINNER_HTML;
            addToCart(variant.id)
              .then(function () {
                btn.innerHTML = CHECK_SVG;
                btn.classList.add('cdc-qa__size--added');
                setTimeout(function () {
                  btn.textContent = original;
                  btn.classList.remove('cdc-qa__size--added');
                  btn.dataset.busy = '';
                }, 1400);
                closeMobilePanel(wrapper);
              })
              .catch(function () {
                btn.textContent = original;
                btn.dataset.busy = '';
              });
          } else {
            selections[opt.name] = valName;
            row.querySelectorAll('button').forEach(function (b) {
              b.classList.remove('cdc-qa__swatch--active');
              b.classList.remove('cdc-qa__size--active');
            });
            btn.classList.add(isColor ? 'cdc-qa__swatch--active' : 'cdc-qa__size--active');
            refreshAvailability();
          }
        });

        row.appendChild(btn);
      });

      rows.push({ row: row, opt: opt, isTrigger: isTrigger, isColor: isColor });
      panel.appendChild(row);
    });

    function refreshAvailability() {
      rows.forEach(function (entry) {
        if (!entry.isTrigger) return;
        entry.row.querySelectorAll('button').forEach(function (b) {
          b.disabled = !optionAvailable(data, selections, entry.opt.name, b.dataset.value);
        });
      });
    }

    rows.forEach(function (entry) {
      if (entry.isTrigger) return;
      var selected = selections[entry.opt.name];
      if (!selected) return;
      var btn = entry.row.querySelector('button[data-value="' + CSS.escape(selected) + '"]');
      if (btn) btn.classList.add(entry.isColor ? 'cdc-qa__swatch--active' : 'cdc-qa__size--active');
    });

    refreshAvailability();
    panel.hidden = false;
  }

  function closeMobilePanel(wrapper) {
    var panel = wrapper.querySelector('[data-cdc-qa-panel]');
    var plus = wrapper.querySelector('.cdc-qa__plus');
    if (panel) panel.classList.remove('cdc-qa__panel--open');
    if (plus) plus.setAttribute('aria-expanded', 'false');
  }

  function closeAllPanels(except) {
    document.querySelectorAll('.cdc-qa__panel--open').forEach(function (panel) {
      var wrapper = panel.closest('[data-cdc-qa]');
      if (wrapper && wrapper !== except) closeMobilePanel(wrapper);
    });
  }

  /* --------------------------------------------------------- splide load */

  var splidePromise = null;

  function loadSplide() {
    if (window.Splide) return Promise.resolve();
    if (splidePromise) return splidePromise;
    splidePromise = new Promise(function (resolve, reject) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/@splidejs/splide@' + SPLIDE_VERSION + '/dist/css/splide.min.css';
      document.head.appendChild(link);

      var script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@splidejs/splide@' + SPLIDE_VERSION + '/dist/js/splide.min.js';
      script.onload = resolve;
      script.onerror = function () {
        splidePromise = null;
        reject(new Error('Splide failed to load'));
      };
      document.head.appendChild(script);
    });
    return splidePromise;
  }

  /* ---------------------------------------------------------- modal mode */

  var modal = null;

  function getModal() {
    if (modal) return modal;
    var root = document.getElementById('cdc-qa-popup-root');
    var el = root ? root.querySelector('[data-cdc-qam]') : null;
    if (!el) return null;

    modal = {
      root: root,
      el: el,
      moneyFormat: root.dataset.moneyFormat || '${{amount}}',
      imageLayout: el.dataset.imageLayout === 'slider' ? 'slider' : 'grid',
      grid: el.querySelector('[data-cdc-qam-grid]'),
      splideEl: el.querySelector('[data-cdc-qam-splide]'),
      slides: el.querySelector('[data-cdc-qam-slides]'),
      title: el.querySelector('[data-cdc-qam-title]'),
      price: el.querySelector('[data-cdc-qam-price]'),
      subtitle: el.querySelector('[data-cdc-qam-subtitle]'),
      options: el.querySelector('[data-cdc-qam-options]'),
      urgency: el.querySelector('[data-cdc-qam-urgency]'),
      urgencyText: el.querySelector('[data-cdc-qam-urgency-text]'),
      atc: el.querySelector('[data-cdc-qam-atc]'),
      atcLabel: el.querySelector('[data-cdc-qam-atc-label]'),
      atcSpinner: el.querySelector('[data-cdc-qam-atc-spinner]'),
      link: el.querySelector('[data-cdc-qam-link]'),
      loading: el.querySelector('[data-cdc-qam-loading]'),
      sgOverlay: document.querySelector('[data-cdc-qam-sg]'),
      splideInstance: null,
      data: null,
      selections: {},
      sizeOptionIndex: 0
    };

    el.querySelectorAll('[data-cdc-qam-close]').forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.getAttribute('aria-hidden') === 'false') closeModal();
    });

    if (modal.sgOverlay) {
      var sgClose = modal.sgOverlay.querySelector('[data-cdc-qam-sg-close]');
      if (sgClose) sgClose.addEventListener('click', closeSizeGuide);
      modal.sgOverlay.addEventListener('click', function (e) {
        if (e.target === modal.sgOverlay) closeSizeGuide();
      });
    }

    modal.atc.addEventListener('click', function () {
      if (!modal.data) return;
      var variant = findVariant(modal.data, modal.selections);
      if (!variant || !variant.available) return;
      modal.atc.disabled = true;
      modal.atcLabel.hidden = true;
      modal.atcSpinner.hidden = false;
      addToCart(variant.id)
        .then(function () {
          modal.atcSpinner.hidden = true;
          modal.atcLabel.hidden = false;
          closeModal();
        })
        .catch(function () {
          modal.atcSpinner.hidden = true;
          modal.atcLabel.hidden = false;
          modal.atcLabel.textContent = 'Error, try again';
          setTimeout(updateModalState, 2000);
        });
    });

    return modal;
  }

  function openSizeGuide() {
    if (!modal || !modal.sgOverlay) return;
    modal.sgOverlay.classList.add('cdc-qam__sg-overlay--open');
  }

  function closeSizeGuide() {
    if (!modal || !modal.sgOverlay) return;
    modal.sgOverlay.classList.remove('cdc-qam__sg-overlay--open');
  }

  function imagesForSelection() {
    var data = modal.data;
    var colorOptIndex = -1;
    data.options.forEach(function (opt, i) {
      if (optionIsColor(opt, modal.swatchMap)) colorOptIndex = i;
    });

    if (colorOptIndex === -1) return data.images;

    var colorName = null;
    data.options.forEach(function (opt) {
      if (optionIsColor(opt, modal.swatchMap)) colorName = modal.selections[opt.name];
    });
    if (!colorName) return data.images;

    var colorLower = colorName.trim().toLowerCase();
    var matched = data.images.filter(function (img) {
      return img.alt && img.alt.trim().toLowerCase() === colorLower;
    });

    if (!matched.length) {
      data.variants
        .filter(function (v) { return v.options[colorOptIndex] === colorName && v.featured_image; })
        .forEach(function (v) {
          if (!matched.find(function (i) { return i.src === v.featured_image.src; })) {
            matched.push({ src: v.featured_image.src, alt: data.title });
          }
        });
    }
    return matched.length ? matched : data.images;
  }

  function appendImage(parent, imgData, wrapInLi) {
    var container = parent;
    if (wrapInLi) {
      container = document.createElement('li');
      container.className = 'splide__slide';
      parent.appendChild(container);
    }
    var frame = document.createElement('div');
    frame.className = 'cdc-qam__img is-loading';
    var img = document.createElement('img');
    img.alt = imgData.alt || (modal.data ? modal.data.title : '');
    img.loading = 'eager';
    img.decoding = 'async';
    img.addEventListener('load', function () { frame.classList.remove('is-loading'); }, { once: true });
    img.addEventListener('error', function () { frame.classList.remove('is-loading'); }, { once: true });
    img.src = imgData.src;
    if (img.complete && img.naturalWidth) frame.classList.remove('is-loading');
    frame.appendChild(img);
    container.appendChild(frame);
  }

  function renderImages() {
    var imgs = imagesForSelection();

    if (modal.imageLayout === 'slider' && window.Splide) {
      if (modal.splideInstance) {
        modal.splideInstance.destroy(true);
        modal.splideInstance = null;
      }
      modal.slides.innerHTML = '';
      imgs.slice(0, 10).forEach(function (imgData) {
        appendImage(modal.slides, imgData, true);
      });
      modal.splideInstance = new window.Splide(modal.splideEl, {
        type: imgs.length > 1 ? 'loop' : 'slide',
        perPage: 1,
        arrows: imgs.length > 1,
        pagination: imgs.length > 1,
        gap: 0,
        drag: imgs.length > 1,
        speed: 450
      }).mount();
      return;
    }

    /* Grid fallback (also used if Splide failed to load) */
    modal.grid.hidden = false;
    if (modal.splideEl) modal.splideEl.hidden = true;
    modal.grid.innerHTML = '';
    var toShow = imgs.slice(0, 4);
    for (var i = 0; i < 4; i += 1) {
      if (toShow[i]) {
        appendImage(modal.grid, toShow[i], false);
      } else {
        var placeholder = document.createElement('div');
        placeholder.className = 'cdc-qam__img cdc-qam__img--empty';
        modal.grid.appendChild(placeholder);
      }
    }
  }

  function updateModalState() {
    var data = modal.data;
    var allSelected = data.options.every(function (opt) { return modal.selections[opt.name]; });

    /* Refresh disabled state on size buttons for the selected color */
    modal.options.querySelectorAll('[data-option-row]').forEach(function (row) {
      if (row.dataset.isColor === 'true') return;
      row.querySelectorAll('button[data-value]').forEach(function (b) {
        b.disabled = !optionAvailable(data, modal.selections, row.dataset.optionName, b.dataset.value);
        b.classList.toggle('cdc-qam__opt--soldout', b.disabled);
      });
    });

    if (!allSelected) {
      modal.urgency.hidden = true;
      modal.atc.disabled = true;
      modal.atcLabel.textContent = 'Select options';
      return;
    }

    var variant = findVariant(data, modal.selections);
    if (variant) {
      var priceHtml = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml = '<s class="cdc-qam__price-compare">' + formatMoney(variant.compare_at_price, modal.moneyFormat) + '</s> '
          + '<span class="cdc-qam__price-sale">' + formatMoney(variant.price, modal.moneyFormat) + '</span>';
      } else {
        priceHtml = '<span>' + formatMoney(variant.price, modal.moneyFormat) + '</span>';
      }
      modal.price.innerHTML = priceHtml;
    }

    if (variant && variant.available) {
      modal.atc.disabled = false;
      modal.atcLabel.textContent = 'Add to bag';

      var sizeValue = variant.options[modal.sizeOptionIndex] || '';
      modal.urgencyText.textContent = 'Only ' + urgencyCount(data.id, variant.id) + ' left in size ' + sizeValue;
      modal.urgency.hidden = false;
    } else {
      modal.urgency.hidden = true;
      modal.atc.disabled = true;
      modal.atcLabel.textContent = 'Sold out';
    }
  }

  function selectModalOption(optName, value, row, isColor) {
    modal.selections[optName] = value;
    row.querySelectorAll('button[data-value]').forEach(function (b) {
      b.classList.remove('cdc-qam__opt--active');
      b.classList.remove('cdc-qam__swatch--active');
    });
    var active = row.querySelector('button[data-value="' + CSS.escape(value) + '"]');
    if (active) active.classList.add(isColor ? 'cdc-qam__swatch--active' : 'cdc-qam__opt--active');

    if (isColor) {
      var nameEl = row.parentElement.querySelector('[data-cdc-qam-color-name]');
      if (nameEl) nameEl.textContent = value;
      renderImages();
    }
    updateModalState();
  }

  function populateModal(data, preselectVariant) {
    modal.data = data;
    modal.selections = {};
    modal.title.textContent = data.title;
    modal.price.innerHTML = '<span>' + formatMoney(data.price, modal.moneyFormat) + '</span>';
    modal.subtitle.textContent = data.description_short || '';
    modal.subtitle.hidden = !data.description_short;
    modal.link.href = data.url;
    modal.options.innerHTML = '';
    modal.urgency.hidden = true;

    modal.sizeOptionIndex = 0;
    data.options.forEach(function (opt, i) {
      if (/^size$/i.test(opt.name)) modal.sizeOptionIndex = i;
    });

    /* Size first, then color, then anything else */
    var sorted = data.options.map(function (opt, oi) { return { opt: opt, oi: oi }; });
    sorted.sort(function (a, b) {
      function rank(o) {
        if (/^size$/i.test(o.opt.name)) return 0;
        if (optionIsColor(o.opt, modal.swatchMap)) return 1;
        return 2;
      }
      return rank(a) - rank(b);
    });

    sorted.forEach(function (entry) {
      var opt = entry.opt;
      var oi = entry.oi;
      var isColor = optionIsColor(opt, modal.swatchMap);
      var isSize = /^size$/i.test(opt.name);

      var group = document.createElement('div');
      group.className = 'cdc-qam__option-group';

      var labelRow = document.createElement('div');
      labelRow.className = 'cdc-qam__option-label-row';
      var label = document.createElement('span');
      label.className = 'cdc-qam__option-label';
      label.textContent = opt.name;
      labelRow.appendChild(label);

      if (isColor) {
        var colorName = document.createElement('span');
        colorName.className = 'cdc-qam__option-value-name';
        colorName.setAttribute('data-cdc-qam-color-name', '');
        labelRow.appendChild(colorName);
      }

      if (isSize) {
        var sgBtn = document.createElement('button');
        sgBtn.type = 'button';
        sgBtn.className = 'cdc-qam__sg-btn';
        sgBtn.textContent = 'Size guide';
        sgBtn.addEventListener('click', openSizeGuide);
        labelRow.appendChild(sgBtn);
      }
      group.appendChild(labelRow);

      var row = document.createElement('div');
      row.className = 'cdc-qam__option-values' + (isColor ? ' cdc-qam__option-values--swatches' : '');
      row.setAttribute('data-option-row', '');
      row.dataset.optionName = opt.name;
      row.dataset.isColor = String(isColor);

      opt.values.forEach(function (value) {
        var valName = typeof value === 'object' ? (value.value || String(value)) : String(value);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.value = valName;
        if (isColor) {
          btn.className = 'cdc-qam__swatch';
          btn.setAttribute('data-swatch', valName.replace(/"/g, '').toLowerCase());
          applySwatch(btn, modal.swatchMap, valName);
          btn.setAttribute('aria-label', valName);
          btn.title = valName;
        } else {
          btn.className = 'cdc-qam__opt';
          btn.textContent = valName;
        }
        btn.addEventListener('click', function () {
          selectModalOption(opt.name, valName, row, isColor);
        });
        row.appendChild(btn);
      });

      group.appendChild(row);
      modal.options.appendChild(group);
    });

    /* Preselect the card variant (or first available) so one click adds */
    var target = preselectVariant
      || data.variants.find(function (v) { return v.available; })
      || data.variants[0];
    if (target) {
      data.options.forEach(function (opt, i) {
        var row = modal.options.querySelector('[data-option-row][data-option-name="' + CSS.escape(opt.name) + '"]');
        if (row) selectModalOption(opt.name, target.options[i], row, optionIsColor(opt, modal.swatchMap));
      });
      renderImages();
    } else {
      renderImages();
      updateModalState();
    }
  }

  function openModal(data, wrapper) {
    var m = getModal();
    if (!m) return;

    m.swatchMap = swatchMap(wrapper);

    var preselectVariant = null;
    if (wrapper) {
      var preId = parseVariantParam(wrapper.dataset.productUrl);
      if (preId) {
        preselectVariant = data.variants.find(function (v) { return v.id === preId; });
      }
    }

    function show() {
      populateModal(data, preselectVariant);
      m.el.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    if (m.imageLayout === 'slider') {
      loadSplide().then(show).catch(function () {
        m.imageLayout = 'grid';
        show();
      });
    } else {
      show();
    }
  }

  function closeModal() {
    if (!modal) return;
    modal.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    closeSizeGuide();
    if (modal.splideInstance) {
      modal.splideInstance.destroy(true);
      modal.splideInstance = null;
    }
    modal.data = null;
  }

  /* --------------------------------------------------------- prefetching */

  /* In inline mode, prebuild panels as cards scroll into view so the
     hover reveal is instant instead of waiting on the first fetch */
  var panelObserver = null;

  function observeAll() {
    if (currentMode() !== 'inline' || !('IntersectionObserver' in window)) return;
    if (!panelObserver) {
      panelObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var wrapper = entry.target;
          panelObserver.unobserve(wrapper);
          ensureData(wrapper)
            .then(function (data) {
              if (currentMode() === 'inline') buildPanel(wrapper, data);
            })
            .catch(function () {});
        });
      }, { rootMargin: '200px' });
    }
    document.querySelectorAll('[data-cdc-qa]').forEach(function (wrapper) {
      if (wrapper.dataset.observed === 'true') return;
      wrapper.dataset.observed = 'true';
      panelObserver.observe(wrapper);
    });
  }

  document.addEventListener('shopify:section:load', observeAll);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAll);
  } else {
    observeAll();
  }

  /* Prefetch on hover so opening is instant */
  document.addEventListener('mouseover', function (e) {
    if (currentMode() === 'theme') return;
    var wrapper = e.target.closest ? e.target.closest('[data-cdc-qa]') : null;
    if (!wrapper) {
      var block = e.target.closest ? e.target.closest('.product-block') : null;
      if (block) wrapper = block.querySelector('[data-cdc-qa]');
    }
    if (!wrapper || wrapper.dataset.prefetched === 'true') return;
    wrapper.dataset.prefetched = 'true';
    ensureData(wrapper)
      .then(function (data) {
        if (currentMode() === 'inline') {
          buildPanel(wrapper, data);
        } else {
          preloadImages(data, 8);
          if (getModal() && modal.imageLayout === 'slider') loadSplide().catch(function () {});
        }
      })
      .catch(function () {
        wrapper.dataset.prefetched = '';
      });
  });

  /* ------------------------------------------------------------- clicks */

  function setTriggerBusy(trigger, busy) {
    if (trigger.classList.contains('cdc-qa__plus')) return;
    if (busy) {
      trigger.dataset.originalText = trigger.textContent;
      trigger.innerHTML = SPINNER_HTML;
    } else if (trigger.dataset.originalText) {
      trigger.textContent = trigger.dataset.originalText;
    }
  }

  document.addEventListener('click', function (e) {
    var mode = currentMode();
    if (mode === 'theme') return;

    var trigger = e.target.closest ? e.target.closest('.cdc-qa__trigger, .cdc-qa__plus') : null;
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      var wrapper = trigger.closest('[data-cdc-qa]');
      if (!wrapper || trigger.dataset.busy === 'true') return;

      if (mode === 'modal') {
        trigger.dataset.busy = 'true';
        setTriggerBusy(trigger, true);
        ensureData(wrapper)
          .then(function (data) {
            preloadImages(data, 8);
            openModal(data, wrapper);
          })
          .catch(function () {
            /* Fall back to the product page if the fetch fails */
            window.location.href = wrapper.dataset.productUrl;
          })
          .finally(function () {
            trigger.dataset.busy = '';
            setTriggerBusy(trigger, false);
          });
        return;
      }

      /* inline mode: plus button toggles the panel on mobile */
      trigger.dataset.busy = 'true';
      ensureData(wrapper)
        .then(function (data) {
          buildPanel(wrapper, data);
          var panel = wrapper.querySelector('[data-cdc-qa-panel]');
          var isOpen = panel.classList.contains('cdc-qa__panel--open');
          closeAllPanels(wrapper);
          panel.classList.toggle('cdc-qa__panel--open', !isOpen);
          trigger.setAttribute('aria-expanded', String(!isOpen));
        })
        .catch(function () {
          window.location.href = wrapper.dataset.productUrl;
        })
        .finally(function () {
          trigger.dataset.busy = '';
        });
      return;
    }

    /* Tap outside closes open mobile panels */
    if (!e.target.closest || !e.target.closest('[data-cdc-qa]')) {
      closeAllPanels(null);
    }
  });

  window.CDCQuickAdd = {
    setMode: setMode,
    getMode: currentMode,
    setTheme: setTheme
  };
})();
